/**
 * ECharts heatmap renderer — best for spotting patterns across two dimensions.
 *
 * X axis: groupBy dimension (e.g., fiscal_year)
 * Y axis: seriesBy dimension (e.g., macom or office)
 * Cell color: metric value intensity
 *
 * If no seriesBy is set, falls back to showing groupBy vs first metric
 * as a single-row heatmap.
 */
import ReactECharts from 'echarts-for-react'
import type { PerfRow, MetricKey, GroupByDimension } from './types'
import { METRIC_LABELS, formatMetric, HIERARCHY_DIMENSIONS, getDimensionLabel } from './types'
import { makeChartReady } from './echarts-theme'

interface Props {
  data: PerfRow[]
  groupBy: GroupByDimension
  metrics: MetricKey[]
  seriesBy?: GroupByDimension
  onDrillDown?: (code: string, label: string) => void
  // Accepted for interface compatibility but not rendered (heatmaps don't suit comparisons)
  groupComparisons?: unknown
  primaryLabel?: string
  /** ECharts group key for cross-chart hover sync (Phase F1). */
  chartGroup?: string
}

const getLabel = getDimensionLabel

function getCode(row: PerfRow, dim: GroupByDimension): string {
  if (HIERARCHY_DIMENSIONS.has(dim) && row.entity_code) return String(row.entity_code)
  return String(row[dim] ?? '')
}

export default function HeatmapRenderer({ data, groupBy, metrics, seriesBy, onDrillDown, chartGroup }: Props) {
  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full text-sm text-gray-400">
        No data
      </div>
    )
  }

  const metric = metrics[0]

  if (!seriesBy) {
    // Single-row heatmap — groupBy on X, metric as color
    const categories = data.map((r) => getLabel(r, groupBy))
    const values = data.map((r, i) => {
      const val = typeof r[metric] === 'number' ? (r[metric] as number) : 0
      return [i, 0, val]
    })
    const allValues = values.map((v) => v[2] as number)
    const min = Math.min(...allValues)
    const max = Math.max(...allValues)

    return (
      <ReactECharts
        option={{
          tooltip: {
            formatter: (params: any) => {
              const [x] = params.data
              return `<b>${categories[x]}</b><br/>${METRIC_LABELS[metric]}: <b>${formatMetric(metric, params.data[2])}</b>`
            },
          },
          grid: { left: 60, right: 40, top: 10, bottom: 40 },
          xAxis: { type: 'category', data: categories, axisLabel: { fontSize: 10, rotate: categories.length > 8 ? 45 : 0 } },
          yAxis: { type: 'category', data: [METRIC_LABELS[metric]], axisLabel: { fontSize: 10 } },
          visualMap: {
            min,
            max,
            calculable: true,
            orient: 'horizontal',
            left: 'center',
            bottom: 0,
            inRange: { color: ['#f0f9ff', '#3b82f6', '#1e3a5f'] },
            textStyle: { fontSize: 10 },
          },
          series: [{
            type: 'heatmap',
            data: values,
            label: { show: true, fontSize: 10, formatter: (p: any) => formatMetric(metric, p.data[2]) },
          }],
        }}
        style={{ height: '100%', width: '100%' }}
        opts={{ renderer: 'canvas' }}
        onChartReady={makeChartReady(chartGroup)}
      />
    )
  }

  // Two-dimensional heatmap: groupBy on X, seriesBy on Y
  const xLabels: string[] = []
  const xSet = new Set<string>()
  const yLabels: string[] = []
  const yCodes: string[] = []
  const ySet = new Set<string>()
  const valueMap = new Map<string, number>()

  for (const row of data) {
    const xLabel = getLabel(row, groupBy)
    const yLabel = getLabel(row, seriesBy)
    const yCode = getCode(row, seriesBy)
    if (!xSet.has(xLabel)) { xSet.add(xLabel); xLabels.push(xLabel) }
    if (!ySet.has(yLabel)) { ySet.add(yLabel); yLabels.push(yLabel); yCodes.push(yCode) }
    const val = typeof row[metric] === 'number' ? (row[metric] as number) : 0
    valueMap.set(`${xLabel}|${yLabel}`, val)
  }

  const heatData: [number, number, number][] = []
  const allValues: number[] = []
  for (let xi = 0; xi < xLabels.length; xi++) {
    for (let yi = 0; yi < yLabels.length; yi++) {
      const val = valueMap.get(`${xLabels[xi]}|${yLabels[yi]}`) ?? 0
      heatData.push([xi, yi, val])
      allValues.push(val)
    }
  }
  const min = Math.min(...allValues)
  const max = Math.max(...allValues)

  // Determine color range based on metric direction
  const isPctMetric = metric.endsWith('_pct')
  const colorRange = isPctMetric
    ? ['#fef2f2', '#ef4444', '#22c55e']   // red-to-green for percentages
    : ['#f0f9ff', '#3b82f6', '#1e3a5f']   // light-to-dark blue

  const onEvents = onDrillDown
    ? {
        click: (params: any) => {
          const [, yi] = params.data
          if (yi !== undefined && yCodes[yi]) {
            onDrillDown(yCodes[yi], yLabels[yi])
          }
        },
      }
    : undefined

  return (
    <ReactECharts
      option={{
        tooltip: {
          formatter: (params: any) => {
            const [xi, yi, val] = params.data
            return `<b>${yLabels[yi]}</b> × ${xLabels[xi]}<br/>${METRIC_LABELS[metric]}: <b>${formatMetric(metric, val)}</b>`
          },
        },
        grid: {
          left: Math.min(200, Math.max(...yLabels.map((l) => l.length)) * 6 + 20),
          right: 60,
          top: 10,
          bottom: 60,
        },
        xAxis: {
          type: 'category',
          data: xLabels,
          axisLabel: { fontSize: 10, rotate: xLabels.length > 8 ? 45 : 0 },
          splitArea: { show: true },
        },
        yAxis: {
          type: 'category',
          data: yLabels,
          axisLabel: { fontSize: 10, width: 180, overflow: 'truncate' },
          splitArea: { show: true },
        },
        visualMap: {
          min,
          max,
          calculable: true,
          orient: 'horizontal',
          left: 'center',
          bottom: 0,
          inRange: { color: colorRange },
          textStyle: { fontSize: 10 },
        },
        series: [{
          type: 'heatmap',
          data: heatData,
          label: {
            show: heatData.length < 100,
            fontSize: 9,
            formatter: (p: any) => formatMetric(metric, p.data[2]),
          },
          emphasis: { itemStyle: { shadowBlur: 10, shadowColor: 'rgba(0, 0, 0, 0.3)' } },
        }],
      }}
      style={{ height: '100%', width: '100%' }}
      opts={{ renderer: 'canvas' }}
      onEvents={onEvents}
      onChartReady={makeChartReady(chartGroup)}
    />
  )
}
